import { Link } from "react-router-dom";
import { MapPin, Check } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { CtaBand } from "@/components/CtaBand";
import { SERVICE_AREAS } from "@/data/suburbs";
import { container, sectionPad, cardShadow, btnPrimary } from "@/lib/ui";
import { BUSINESS } from "@/data/business";
import { localBusinessSchema, breadcrumbSchema } from "@/lib/schema";

export default function ServiceAreasPage() {
  return (
    <>
      <Seo
        title="Service Areas — House Cleaning Melbourne (West, East & North)"
        description="JK House Cleaning provides professional house cleaning across West Melbourne, East Melbourne and North Melbourne. Trusted local house cleaners in Melbourne — get a free quote."
        path="/service-areas"
        schema={[localBusinessSchema(), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Service Areas", path: "/service-areas" }])]}
      />
      <PageHeader
        eyebrow="Service Areas"
        title="House cleaning across Melbourne"
        subtitle="JK House Cleaning proudly services homes across West Melbourne, East Melbourne and North Melbourne."
        crumbs={[{ name: "Service Areas" }]}
      />

      <section className={`${sectionPad} bg-white`}>
        <div className={container}>
          <div className="max-w-3xl">
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-navy">
              Trusted house cleaners in Melbourne
            </h2>
            <p className="mt-4 text-gray-600 leading-relaxed">
              As a local Melbourne business, we deliver reliable, detail-focused house cleaning to
              households right across the city. Whether you're a busy family, a professional, an
              Airbnb host or preparing for end of lease, our team leaves your home spotless and fresh.
            </p>
            <p className="mt-4 text-gray-600 leading-relaxed">
              We currently focus on three service areas — West Melbourne, East Melbourne and North
              Melbourne — so we can offer dependable scheduling and a consistently high standard of clean.
            </p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICE_AREAS.map((s) => (
              <div
                key={s.name}
                className={`flex flex-col overflow-hidden rounded-2xl bg-white border border-gray-100 ${cardShadow}`}
                data-testid={`area-card-${s.name.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={s.image} alt={`House cleaning in ${s.name}`} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
                  <div className="absolute bottom-3 left-4 flex items-center gap-2 text-white">
                    <MapPin className="w-4 h-4 text-brandgreen-light" />
                    <span className="font-heading font-semibold">{s.name}</span>
                  </div>
                </div>
                <div className="p-5 flex-1">
                  <p className="text-sm text-gray-600 leading-relaxed">{s.blurb}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 rounded-2xl bg-neutralbg/70 border border-gray-100 p-8">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-heading text-xl font-semibold text-navy">Why locals choose JK House Cleaning</h2>
                <ul className="mt-5 space-y-3">
                  {["ABN registered & fully insured", "Police checked for your security", "Own professional equipment & products supplied", "Flexible weekly, fortnightly, monthly & one-off cleaning", "Free, personalised quotes"].map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brandgreen shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:text-right">
                <p className="text-gray-600 leading-relaxed">
                  Not sure if we can reach your home? Just ask when you request your free quote and we'll let you know.
                </p>
                <Link to="/quote" className={`${btnPrimary} mt-6`} data-testid="areas-page-quote">
                  Get a Free Quote
                </Link>
                <p className="mt-4 text-sm text-gray-500">Or call {BUSINESS.phoneDisplay} — Mon–Fri, 9:00 AM – 4:30 PM.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
